import { useState } from 'react';
import { DocumentTextIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';

import { Card, Spinner } from '@realvisor/ui';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';

interface Document {
  id: string;
  title: string;
  description: string | null;
  content: string | null;
  category: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function CallScripts() {
  const { can } = usePermissions();
  const canEdit = can('call_scripts:write');
  const [selectedScript, setSelectedScript] = useState<Document | null>(null);

  // Fetch guides/documents tagged as call scripts
  const { data: scripts, isLoading } = useQuery({
    queryKey: ['call-scripts'],
    queryFn: () => api.get<Document[]>('/documents?category=call_script'),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Call skripty</h1>
          <p className="mt-1 text-sm text-gray-500">
            Šablony a skripty pro hovory
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : !scripts || scripts.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-8">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-3 text-sm text-gray-500">
              Zatím nebyly vytvořeny žádné call skripty.
            </p>
            {canEdit && (
              <p className="mt-1 text-sm text-gray-400">
                Vytvořte skripty v sekci Návody s kategorií &quot;call_script&quot;.
              </p>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Script list */}
          <div className="lg:col-span-1 space-y-2">
            {scripts.map((script) => (
              <button
                key={script.id}
                onClick={() => setSelectedScript(script)}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  selectedScript?.id === script.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <DocumentTextIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="truncate text-sm font-medium text-gray-900">
                    {script.title}
                  </span>
                </div>
                {script.description && (
                  <p className="mt-1 truncate text-xs text-gray-500 pl-6">
                    {script.description}
                  </p>
                )}
              </button>
            ))}
          </div>

          {/* Script content */}
          <div className="lg:col-span-2">
            {selectedScript ? (
              <Card padding="lg">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedScript.title}
                  </h2>
                  {canEdit && (
                    <button className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
                      <PencilIcon className="h-4 w-4" />
                      Upravit
                    </button>
                  )}
                </div>
                <div className="prose prose-sm max-w-none text-gray-700">
                  {selectedScript.content || 'Skript nemá obsah.'}
                </div>
              </Card>
            ) : (
              <Card padding="lg">
                <div className="text-center py-12 text-sm text-gray-500">
                  Vyberte skript ze seznamu
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
